import { useEffect, useState } from 'react'
import { nextSyncStallDeadline, type ConnectionSyncState } from '@/lib/ledgersync-sync-activity'

/** Largest delay `setTimeout` honours (signed 32-bit ms, ≈24.8 days). */
const MAX_TIMER_DELAY_MS = 2 ** 31 - 1

/**
 * Re-render the calling component when the soonest active LedgerSync pipeline
 * among `rows` goes stale (DCP-401).
 *
 * A dead run (Crashed / Canceled — no terminal status is ever written) stops
 * counting as active at `*_status_at + LEDGERSYNC_SYNC_STALL_MS` without any
 * data change, so without this nothing re-renders at that moment and the
 * spinner and disabled Sync stay up for the rest of the session. See
 * `nextSyncStallDeadline` for the full reasoning.
 *
 * One timer at a time, for the EARLIEST deadline. When it fires the state bump
 * re-renders the caller, the effect re-runs with a fresh `now`, and the next
 * deadline (if any row is still active) is scheduled. The timer is re-armed
 * whenever `rows` changes and cleared on unmount.
 *
 * @param rows - The connection list the component renders
 * @returns A counter that changes at each deadline, for a caller that caches
 *   derived sync state in a memo and needs it as a dependency. Callers that
 *   derive at render time (both connection screens) can ignore it.
 */
export function useSyncStallRerender(rows: readonly ConnectionSyncState[] | undefined): number {
  const [tick, setTick] = useState(0)

  useEffect(() => {
    const deadline = nextSyncStallDeadline(rows)
    if (deadline === null) return
    // +1 ms: the shared predicate is `age < ceiling`, so at exactly the
    // deadline the row is ALREADY stale; firing a hair later guarantees the
    // re-render reads it that way even with coarse timer resolution.
    //
    // Clamped to the largest delay timers accept: above 2^31−1 ms a browser
    // treats the delay as 0, and a far-future deadline (a worker clock far
    // ahead) would then re-render in a tight loop. A clamped timer just fires
    // early, finds the row still active, and re-arms.
    const delay = Math.min(Math.max(0, deadline - Date.now()) + 1, MAX_TIMER_DELAY_MS)
    const timer = setTimeout(() => setTick((n) => n + 1), delay)
    return () => clearTimeout(timer)
  }, [rows, tick])

  return tick
}
